import React, { useEffect, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { IntensityGaugeSlider } from './IntensityGauge';
import { RssSourcePicker } from './RssSourcePicker';

const PLATFORM_OPTIONS = [
  { value: 'reddit', label: 'Reddit' },
  { value: 'bluesky', label: 'Bluesky' },
  { value: 'youtube', label: 'YouTube' },
  { value: 'rss', label: 'RSS' },
];

export interface ListeningRuleEditable {
  id?: string;
  name: string;
  keywords: string[];
  platforms: string[];
  rss_feeds: string[];
  intensity_gauge: number;
  highlight_style: { pin?: boolean; badge?: boolean; email_digest?: boolean };
}

interface ListeningRuleDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  initialValue?: ListeningRuleEditable;
  onSaved: () => void;
}

export const ListeningRuleDialog: React.FC<ListeningRuleDialogProps> = ({
  open,
  onOpenChange,
  initialValue,
  onSaved,
}) => {
  const { user } = useAuth();
  const editing = Boolean(initialValue?.id);

  const [name, setName] = useState('');
  const [keywordsText, setKeywordsText] = useState('');
  const [platforms, setPlatforms] = useState<string[]>([]);
  const [rssFeeds, setRssFeeds] = useState<string[]>([]);
  const [gauge, setGauge] = useState(3);
  const [pin, setPin] = useState(false);
  const [badge, setBadge] = useState(true);
  const [emailDigest, setEmailDigest] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setName(initialValue?.name ?? '');
    setKeywordsText((initialValue?.keywords ?? []).join(', '));
    setPlatforms(initialValue?.platforms ?? []);
    setRssFeeds(initialValue?.rss_feeds ?? []);
    setGauge(initialValue?.intensity_gauge ?? 3);
    setPin(initialValue?.highlight_style?.pin ?? false);
    setBadge(initialValue?.highlight_style?.badge ?? true);
    setEmailDigest(initialValue?.highlight_style?.email_digest ?? false);
  }, [open, initialValue]);

  const togglePlatform = (id: string, on: boolean) => {
    setPlatforms((prev) => (on ? [...new Set([...prev, id])] : prev.filter((p) => p !== id)));
  };

  // If the user picks any RSS source, ensure 'rss' is in platforms so the
  // engine actually applies the rule to RSS items.
  useEffect(() => {
    if (rssFeeds.length > 0 && !platforms.includes('rss')) {
      setPlatforms((prev) => [...prev, 'rss']);
    }
  }, [rssFeeds, platforms]);

  const handleSave = async () => {
    if (!user) return;
    if (!name.trim()) {
      toast.error('Rule name is required');
      return;
    }
    setSaving(true);
    try {
      const keywords = keywordsText
        .split(',')
        .map((k) => k.trim())
        .filter(Boolean);
      const payload: any = {
        user_id: user.id,
        name: name.trim(),
        rule_type: 'listening',
        keywords: keywords.length ? keywords : null,
        platforms: platforms.length ? platforms : null,
        rss_feeds: rssFeeds.length ? rssFeeds : null,
        intensity_gauge: gauge,
        is_active: gauge > 0,
        highlight_style: { pin, badge, email_digest: emailDigest },
      };

      const result = initialValue?.id
        ? await supabase.from('filtering_rules').update(payload).eq('id', initialValue.id)
        : await supabase.from('filtering_rules').insert(payload);

      if (result.error) {
        toast.error(`Failed to save rule: ${result.error.message}`);
        return;
      }
      toast.success(editing ? 'Listening rule updated' : 'Listening rule created');
      onOpenChange(false);
      onSaved();
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{editing ? 'Edit Listening Rule' : 'New Listening Rule'}</DialogTitle>
          <p className="text-sm text-muted-foreground">
            Surface matching content in your feed. Set the gauge to control how prominently it appears.
          </p>
        </DialogHeader>

        <div className="space-y-6">
          <div>
            <Label htmlFor="lr-name">Rule name</Label>
            <Input
              id="lr-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g., AI news, Brand mentions"
            />
          </div>

          <div>
            <Label htmlFor="lr-keywords">Keywords / topics to watch</Label>
            <Textarea
              id="lr-keywords"
              rows={3}
              placeholder="comma-separated; matches title or body, case-insensitive"
              value={keywordsText}
              onChange={(e) => setKeywordsText(e.target.value)}
            />
          </div>

          <IntensityGaugeSlider mode="listening" value={gauge} onChange={setGauge} />

          <div>
            <Label>Platforms</Label>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mt-2">
              {PLATFORM_OPTIONS.map((p) => (
                <label key={p.value} className="flex items-center gap-2 text-sm cursor-pointer">
                  <Switch
                    checked={platforms.includes(p.value)}
                    onCheckedChange={(checked) => togglePlatform(p.value, checked)}
                  />
                  <span>{p.label}</span>
                </label>
              ))}
            </div>
            <p className="text-xs text-muted-foreground mt-1">
              Leave all unchecked to apply across every connected source.
            </p>
          </div>

          <RssSourcePicker value={rssFeeds} onChange={setRssFeeds} />

          <div className="space-y-3 border border-border/60 rounded-md p-4">
            <Label className="text-sm font-medium">Highlight style</Label>
            <div className="space-y-2">
              <label className="flex items-start gap-3 cursor-pointer">
                <Switch checked={pin} onCheckedChange={setPin} />
                <span className="text-sm">
                  <strong>Pin to top.</strong>{' '}
                  <span className="text-muted-foreground">
                    Matched items sort above other feed content.
                  </span>
                </span>
              </label>
              <label className="flex items-start gap-3 cursor-pointer">
                <Switch checked={badge} onCheckedChange={setBadge} />
                <span className="text-sm">
                  <strong>Show rule badge.</strong>{' '}
                  <span className="text-muted-foreground">
                    Matched items display this rule's name as a badge.
                  </span>
                </span>
              </label>
              <label className="flex items-start gap-3 cursor-pointer">
                <Switch checked={emailDigest} onCheckedChange={setEmailDigest} />
                <span className="text-sm">
                  <strong>Email digest.</strong>{' '}
                  <span className="text-muted-foreground">
                    Include matches in your daily summary email (preference saved; delivery not yet wired).
                  </span>
                </span>
              </label>
            </div>
          </div>

          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>
              Cancel
            </Button>
            <Button onClick={handleSave} disabled={saving}>
              {saving ? 'Saving…' : editing ? 'Update rule' : 'Create rule'}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
